import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCoffee } from "@/context/CoffeeContext";
import { CoffeeModal } from "@/components/CoffeeModal";
import { BTN_PRIMARY, BTN_GHOST, ICON_BTN } from "@/lib/styles";
import { Sparkles, X, Loader2, Coffee, Maximize2, Minimize2, Shuffle } from "lucide-react";
import { toast } from "sonner";

const ACTIONS = [
  { id: "expand", label: "Ampliar", desc: "Desarrolla y agrega detalles", Icon: Maximize2 },
  { id: "summarize", label: "Resumir", desc: "Deja solo lo esencial", Icon: Minimize2 },
  { id: "restructure", label: "Reorganizar", desc: "Estructura y ordena las ideas", Icon: Shuffle },
];

// mode: "note" | "mindmap"; getText: () => string (markdown actual)
// onApply: ({title, outline}) => void
export function AiImproveModal({ open, mode = "note", getText, onApply, onClose }) {
  const { refresh } = useCoffee();
  const [action, setAction] = useState("expand");
  const [loading, setLoading] = useState(false);
  const [cup, setCup] = useState(null);
  const [gated, setGated] = useState(false);
  const [coffeeOpen, setCoffeeOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setGated(false); setAction("expand");
    api.get("/ai/quota").then(({ data }) => setCup(data)).catch((err) => { if (err.response?.status === 402) setGated(true); });
  }, [open]);

  if (!open) return null;

  const run = async () => {
    const text = (getText?.() || "").trim();
    if (text.length < 3) { toast.info("Escribe algo primero para que la IA pueda mejorarlo"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/ai/improve", { text, action, mode });
      onApply?.({ title: data.title, outline: data.outline });
      toast.success(`¡Mejorado! Te quedan ${data.remaining_ml} ml de café este mes`);
      onClose?.();
    } catch (err) {
      const code = err.response?.status;
      if (code === 402) setGated(true);
      else if (code === 429) toast.error(err.response.data.detail);
      else toast.error(formatApiError(err.response?.data?.detail) || "No se pudo mejorar");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !loading && onClose?.()} data-testid="ai-improve-modal">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-medium text-lg flex items-center gap-2"><Sparkles size={19} className="text-amber-500" /> Mejorar con IA</h3>
          <button onClick={() => onClose?.()} className={ICON_BTN} data-testid="ai-improve-close"><X size={16} /></button>
        </div>

        {gated ? (
          <div className="py-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center mx-auto mb-4"><Coffee size={26} /></div>
            <p className="font-body text-sm text-slate-600 dark:text-slate-300 mb-5">Mejorar con IA necesita <b>café activo</b> ☕.</p>
            <button onClick={() => setCoffeeOpen(true)} className={BTN_PRIMARY} data-testid="ai-improve-recharge"><Coffee size={16} /> Recargar café</button>
          </div>
        ) : (
          <>
            <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-4">Elige cómo quieres que el Escriba mejore tu {mode === "mindmap" ? "mapa" : "nota"}.</p>
            <div className="flex flex-col gap-2 mb-4">
              {ACTIONS.map(({ id, label, desc, Icon }) => (
                <button key={id} onClick={() => setAction(id)} className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${action === id ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10" : "border-slate-200 dark:border-slate-800 hover:border-slate-300"}`} data-testid={`improve-action-${id}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${action === id ? "bg-indigo-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-500"}`}><Icon size={17} /></div>
                  <div><p className="font-heading font-medium text-sm">{label}</p><p className="font-body text-xs text-slate-500 dark:text-slate-400">{desc}</p></div>
                </button>
              ))}
            </div>
            <div className="rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 px-3 py-2.5 mb-4 flex items-center gap-2" data-testid="improve-cost-notice">
              <Coffee size={15} className="text-amber-500 shrink-0" />
              <span className="font-body text-xs text-amber-700 dark:text-amber-300">
                Esto tomará un sorbo de tu taza <b>(−{cup?.sip_ml ?? 10} ml)</b>{cup ? ` · te quedan ${cup.remaining_ml} ml este mes` : ""}.
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => onClose?.()} className={BTN_GHOST} disabled={loading} data-testid="ai-improve-cancel">Cancelar</button>
              <button onClick={run} className={BTN_PRIMARY} disabled={loading} data-testid="ai-improve-run">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Mejorar (−{cup?.sip_ml ?? 10} ml)
              </button>
            </div>
          </>
        )}
      </div>
      <CoffeeModal open={coffeeOpen} onClose={() => { setCoffeeOpen(false); refresh(); setGated(false); api.get("/ai/quota").then(({ data }) => setCup(data)).catch(() => {}); }} />
    </div>
  );
}
